import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, InputAdornment, Chip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, Alert, Snackbar, Tooltip, Badge, LinearProgress, CircularProgress,
  Card, CardContent, Grid, Autocomplete, Divider, Menu, Fade, alpha,
  useTheme, useMediaQuery,
} from '@mui/material';
import { useAgenda } from '../hooks/useAgenda';
import {
  Add as AddIcon, Search as SearchIcon, KeyboardArrowLeft as PrevIcon,
  KeyboardArrowRight as NextIcon, Edit as EditIcon, Delete as DeleteIcon,
  Today as TodayIcon, ViewWeek as WeekIcon, ViewModule as MonthIcon,
  Schedule as ScheduleIcon, LocationOn as LocationIcon, Refresh as RefreshIcon,
  FilterList as FilterIcon, Close as CloseIcon,
  Person as PersonIcon, MedicalServices as MedicalIcon,
  CheckCircle as CheckCircleIcon, PlayArrow as PlayIcon,
  Done as DoneIcon,
  Login as CheckInIcon, EventAvailable as ConfirmIcon,
  Cancel as CancelIcon, AccessTime as TimeIcon,
  Phone as PhoneIcon, EventNote as AppointmentIcon,
  WhatsApp as WhatsAppIcon,
  PersonAdd as PersonAddIcon,
  EventRepeat as ReturnIcon,
  Vaccines as ProcedureIcon,
  Biotech as ExamIcon,
  WarningAmber as EmergencyIcon,
  LocalHospital as SurgeryIcon,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isToday, addWeeks, subWeeks, addDays, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';
import PatientDetailPanel from '../components/PatientDetailPanel';
import PatientFormDialog from '../components/PatientFormDialog';
import BudgetFormDialog from '../components/BudgetFormDialog';
import TeleconsultButton from '../components/Teleconsulta/TeleconsultButton';
import { sendAppointmentConfirmation } from '../services/appointmentConfirmationService';

// São Paulo timezone
const getSaoPauloDate = (): Date => {
  const now = new Date();
  const offset = -3 * 60;
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const sp = new Date(utc + (offset * 60000));
  return new Date(sp.getFullYear(), sp.getMonth(), sp.getDate());
};

const formatDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

type AppointmentTypeDef = {
  label: string;
  color: string;
  Icon: SvgIconComponent;
};

const appointmentTypes: Record<string, AppointmentTypeDef> = {
  consultation: { label: 'Consulta', color: '#14B8A6', Icon: MedicalIcon },
  return: { label: 'Retorno', color: '#3b82f6', Icon: ReturnIcon },
  procedure: { label: 'Procedimento', color: '#8b5cf6', Icon: ProcedureIcon },
  exam: { label: 'Exame', color: '#f59e0b', Icon: ExamIcon },
  emergency: { label: 'Emergência', color: '#ef4444', Icon: EmergencyIcon },
  surgery: { label: 'Cirurgia', color: '#ec4899', Icon: SurgeryIcon },
};

const renderAppointmentTypeIcon = (typeKey: string, fontSize = 18) => {
  const def = appointmentTypes[typeKey];
  const Icon = def?.Icon ?? MedicalIcon;
  const color = def?.color ?? '#14B8A6';
  return <Icon sx={{ fontSize, color, flexShrink: 0 }} />;
};

/** Tags de status — tons pastéis (legibilidade sem saturar o formulário). */
const statusConfig: Record<string, { label: string; color: string; bg: string }> = {
  scheduled: { label: 'Agendado', color: '#1d4ed8', bg: '#e8f0fe' },
  confirmed: { label: 'Confirmado', color: '#0f766e', bg: '#e6f7f5' },
  waiting: { label: 'Na Espera', color: '#b45309', bg: '#fef6e8' },
  in_progress: { label: 'Em Atendimento', color: '#6d28d9', bg: '#f3effc' },
  attended: { label: 'Atendido', color: '#0d9488', bg: '#e6f7f5' },
  completed: { label: 'Concluído', color: '#15803d', bg: '#ecfdf3' },
  cancelled: { label: 'Cancelado', color: '#b91c1c', bg: '#fef2f2' },
  no_show: { label: 'Não Compareceu', color: '#64748b', bg: '#f1f5f9' },
  rescheduled: { label: 'Reagendado', color: '#c2410c', bg: '#fff4ed' },
};

/** Cancelados não ocupam a grade — ficam na faixa abaixo do último horário. */
const isAgendaCancelled = (appt: { status?: string }) => appt?.status === 'cancelled';

// ============ CALENDAR TIME LAYOUT ============
// Janela visível (horas inteiras) vem de clinic_settings; padrão 07–20 no front e na migration.
// Granularidade mínima de agendamento (10 minutos)
const SLOT_MIN = 10;
// Altura por hora em cada visualização → define a densidade (maior = mais leitura, mais scroll)
const WEEK_HOUR_HEIGHT = 104;         // compacto (~1.73 px/min)
const WEEK_PX_PER_MIN = WEEK_HOUR_HEIGHT / 60;
const DAY_HOUR_HEIGHT = 196;           // (~3.27 px/min)
const DAY_PX_PER_MIN = DAY_HOUR_HEIGHT / 60;
/** Vista dia na agenda expandida */
const DAY_HOUR_HEIGHT_EXPANDED_LAYOUT = 168;

/** Largura da coluna de horários (hora à esquerda, minutos à direita para não sobrepô-los) */
const TIME_COL_WEEK_PX = 120;
const TIME_COL_DAY_PX = 138;

const parseTimeToMinutes = (t?: string): number => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

const getApptDuration = (a: any): number => {
  const dur = Number(a?.duration_minutes);
  if (dur && dur > 0) return dur;
  const s = parseTimeToMinutes(a?.start_time);
  const e = parseTimeToMinutes(a?.end_time);
  const d = e - s;
  return d > 0 ? d : 30;
};

/** Horas exibidas na grade: [first..last] inclusivo; vem do GET /clinic/settings */
function normalizeAgendaDisplayHours(raw: any): { first: number; last: number } {
  let first = Number(raw?.agenda_display_first_hour);
  let last = Number(raw?.agenda_display_last_hour);
  if (!Number.isFinite(first) || first < 0 || first > 23) first = 7;
  if (!Number.isFinite(last) || last < 0 || last > 23) last = 20;
  if (last < first) last = first;
  return { first, last };
}

const patientAgeLabel = (appt: any): string | null => {
  const bd = appt.clinic_patients?.birth_date ?? (appt as any).patient_birth_date;
  if (!bd) return null;
  try {
    const today = new Date();
    const birth = new Date(bd + (bd.length <= 10 ? 'T12:00:00' : ''));
    if (Number.isNaN(birth.getTime())) return null;
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    if (age < 0 || age > 150) return null;
    return `${age} ano${age === 1 ? '' : 's'}`;
  } catch {
    return null;
  }
};

const billingModeShortLabel = (appt: any): string =>
  appt?.billing_mode === 'plano' ? 'Plano' : 'Particular';

/** Nome, idade (se houver), Particular/Plano e observações (`notes`) — modo de cobrança fica antes das observações. */
const apptLabelWithNotes = (appt: any, nameFallback: string) => {
  const name = (appt.clinic_patients?.full_name || appt.title || '').trim();
  const displayName = name || nameFallback;
  const age = patientAgeLabel(appt);
  const billing = billingModeShortLabel(appt);
  const obs = (appt.notes || '').replace(/\s+/g, ' ').trim();
  const parts = [displayName, age, billing, obs].filter((p) => p != null && String(p).trim() !== '') as string[];
  return parts.length ? parts.join(' · ') : '';
};

type LayoutInfo = { appt: any; topMin: number; durationMin: number; lane: number; totalLanes: number };

type ClipEntry = { appt: any; vs: number; ve: number };

function clipApptsForCalendar(appts: any[], calStartAbsMin: number, calEndExclusiveMin: number): ClipEntry[] {
  const out: ClipEntry[] = [];
  for (const a of appts) {
    const s = parseTimeToMinutes(a.start_time);
    const e = s + getApptDuration(a);
    const vs = Math.max(s, calStartAbsMin);
    const ve = Math.min(e, calEndExclusiveMin);
    if (ve <= vs) continue;
    out.push({ appt: a, vs, ve });
  }
  return out;
}

// Agrupa trechos visíveis sobrepostos em clusters e distribui em lanes
function computeLayout(appts: ClipEntry[], calStartAbsMin: number): LayoutInfo[] {
  const sorted = [...appts].sort((a, b) => {
    if (a.vs !== b.vs) return a.vs - b.vs;
    return (b.ve - b.vs) - (a.ve - a.vs);
  });
  const clusters: ClipEntry[][] = [];
  let cur: ClipEntry[] = [];
  let curEnd = 0;
  for (const row of sorted) {
    const s = row.vs;
    const e = row.ve;
    if (cur.length > 0 && s >= curEnd) {
      clusters.push(cur);
      cur = [];
      curEnd = 0;
    }
    cur.push(row);
    if (e > curEnd) curEnd = e;
  }
  if (cur.length > 0) clusters.push(cur);

  const out: LayoutInfo[] = [];
  for (const cluster of clusters) {
    const laneEnds: number[] = [];
    const laneAssignments: number[] = [];
    for (const row of cluster) {
      const s = row.vs;
      const e = row.ve;
      let li = laneEnds.findIndex(end => end <= s);
      if (li === -1) {
        li = laneEnds.length;
        laneEnds.push(e);
      } else {
        laneEnds[li] = e;
      }
      laneAssignments.push(li);
    }
    const totalLanes = laneEnds.length;
    cluster.forEach((row: ClipEntry, idx: number) => {
      out.push({
        appt: row.appt,
        topMin: row.vs - calStartAbsMin,
        durationMin: row.ve - row.vs,
        lane: laneAssignments[idx],
        totalLanes,
      });
    });
  }
  return out;
}

export type AgendaProps = {
  expandedLayout?: boolean;
  /** Dentro da teleconsulta (drawer): layout compacto, sem botão de entrar na teleconsulta. */
  embeddedInTeleconsult?: boolean;
  /** Pré-preenche o paciente ao criar nova consulta. */
  defaultPatientId?: string;
  defaultPatientName?: string;
};

/** Mobile (abaixo do breakpoint sm do MUI): primeira carga já em Dia + mesmo layout fluido da agenda expandida. */
function initialCalendarViewForViewport(): 'week' | 'month' | 'day' {
  if (typeof window !== 'undefined' && window.matchMedia('(max-width:599.98px)').matches) {
    return 'day';
  }
  return 'week';
}

const Agenda: React.FC<AgendaProps> = ({
  expandedLayout = false,
  embeddedInTeleconsult = false,
  defaultPatientId,
  defaultPatientName,
}) => {
  const theme = useTheme();
  /** Telas estreitas: mesmo comportamento pedido ao “abrir no celular”. */
  const isAgendaMobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const layoutExpanded = expandedLayout || isAgendaMobileLayout || embeddedInTeleconsult;

  const { user } = useAuth();

  // Determinar se o usuário é profissional com vínculo
  const isProfessionalUser = user?.role === 'professional' && !!user?.professional_id;
  const userProfessionalId = user?.professional_id || '';

  const [currentDate, setCurrentDate] = useState(getSaoPauloDate());
  const [selectedDate, setSelectedDate] = useState(getSaoPauloDate());
  const [view, setView] = useState<'week' | 'month' | 'day'>(initialCalendarViewForViewport);
  const [refreshing, setRefreshing] = useState(false);
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Clinic appointments
  const [appointments, setAppointments] = useState<any[]>([]);
  const [todayAppts, setTodayAppts] = useState<any[]>([]);
  const [loadingAppts, setLoadingAppts] = useState(false);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [apptToDelete, setApptToDelete] = useState<any>(null);
  const [patientFormOpen, setPatientFormOpen] = useState(false);
  const [patientForForm, setPatientForForm] = useState<any | null>(null);
  const [patientPanelKey, setPatientPanelKey] = useState(0);
  const [budgetModalPatient, setBudgetModalPatient] = useState<any>(null);
  /** Incrementado após salvar orçamento na agenda para atualizar o painel do paciente. */
  const [patientPanelSummaryReload, setPatientPanelSummaryReload] = useState(0);

  /** Reagendar: rolagem até bloco data/hora + dica ao usuário */
  const scheduleBlockRef = useRef<HTMLDivElement | null>(null);
  const [rescheduleHint, setRescheduleHint] = useState(false);

  // Search
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [profOptions, setProfOptions] = useState<any[]>([]);
  /** Catálogo de procedimentos (cadastro Clínica › Procedimentos) para preencher observações */
  const [procedureNoteOptions, setProcedureNoteOptions] = useState<any[]>([]);
  const [procedureNotePickerKey, setProcedureNotePickerKey] = useState(0);

  // Quick patient creation
  const [quickPatientOpen, setQuickPatientOpen] = useState(false);
  const [quickPatientSaving, setQuickPatientSaving] = useState(false);
  const [quickPatient, setQuickPatient] = useState({ full_name: '', cpf: '', phone: '55', whatsapp: '55', email: '', birth_date: '' });
  const [allProfessionals, setAllProfessionals] = useState<any[]>([]);

  useEffect(() => {
    if (!embeddedInTeleconsult || !defaultPatientId) return;
    setPatientOptions((prev) => {
      if (prev.some((p: any) => p.id === defaultPatientId)) return prev;
      const label = defaultPatientName?.trim() || 'Paciente';
      return [{ id: defaultPatientId, full_name: label, social_name: label }, ...prev];
    });
  }, [embeddedInTeleconsult, defaultPatientId, defaultPatientName]);

  // Form
  const [form, setForm] = useState({
    patient_id: '', professional_id: '', appointment_date: formatDateStr(getSaoPauloDate()),
    start_time: '09:00', end_time: '09:30', duration_minutes: 30,
    appointment_type: 'consultation', title: '', notes: '', internal_notes: '',
    billing_mode: 'particular' as 'particular' | 'plano',
    total_amount: 0,
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as any });
  const [sendingConfirmation, setSendingConfirmation] = useState(false);
  const [confirmationFeedback, setConfirmationFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  // Also load generic events from old system
  const { events: backendEvents, loading: eventsLoading, loadEvents, createEvent, updateEvent, deleteEvent, clearError, error } = useAgenda();

  // Convert backend events
  const genericEvents = backendEvents.map(e => {
    let date: Date;
    if (typeof (e as any).event_date === 'string') {
      const [y, m, d] = (e as any).event_date.split('-').map(Number);
      date = new Date(y, m - 1, d);
    } else { date = new Date((e as any).event_date); }
    let color = (e as any).color || '#14B8A6';
    if (['#1976d2', '#4285f4', '#3b82f6', '#2563eb', '#6366f1'].includes(color)) color = '#14B8A6';
    return { ...e, date, color, isGenericEvent: true };
  });

  useEffect(() => { loadAllProfessionals(); }, []);

  const [agendaFirstHour, setAgendaFirstHour] = useState(7);
  const [agendaLastHour, setAgendaLastHour] = useState(20);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/settings'));
        if (cancelled || !res.data?.data) return;
        const { first, last } = normalizeAgendaDisplayHours(res.data.data);
        setAgendaFirstHour(first);
        setAgendaLastHour(last);
      } catch {
        /* mantém defaults */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { calStartAbsMin, calEndExclusiveMin, calHours, calTotalMin } = useMemo(() => {
    const first = agendaFirstHour;
    const last = agendaLastHour;
    const h = last - first + 1;
    return {
      calStartAbsMin: first * 60,
      calEndExclusiveMin: (last + 1) * 60,
      calHours: h,
      calTotalMin: h * 60,
    };
  }, [agendaFirstHour, agendaLastHour]);

  const snapYToMinutes = useCallback((y: number, pxPerMin: number): { hour: number; minute: number } => {
    const offsetMin = Math.round((y / pxPerMin) / SLOT_MIN) * SLOT_MIN;
    const clamped = Math.max(0, Math.min(calTotalMin - SLOT_MIN, offsetMin));
    const abs = calStartAbsMin + clamped;
    return { hour: Math.floor(abs / 60), minute: abs % 60 };
  }, [calStartAbsMin, calTotalMin]);

  useEffect(() => {
    const ws = getWeekStart(currentDate);
    const we = getWeekEnd(currentDate);
    if (view === 'month') {
      loadAppointments(formatDateStr(startOfMonth(currentDate)), formatDateStr(endOfMonth(currentDate)));
    } else if (view === 'day') {
      loadAppointments(formatDateStr(selectedDate), formatDateStr(selectedDate));
      } else {
      loadAppointments(formatDateStr(ws), formatDateStr(we));
    }
  }, [currentDate, selectedDate, view, filterProfessional, filterType]);

  useEffect(() => { loadTodayAppointments(); }, []);

  const loadAllProfessionals = async () => {
    try {
      const { data } = await axios.get(`${getApiUrl()}/clinic/professionals`);
      setAllProfessionals(data.data || []);
    } catch (e) { console.error(e); }
  };

  const loadAppointments = async (from: string, to: string) => {
    try {
      setLoadingAppts(true);
      const params: any = { date_from: from, date_to: to, limit: 500 };
      // Se o usuário é profissional vinculado, sempre filtrar pelo seu professional_id
      if (isProfessionalUser) {
        params.professional_id = userProfessionalId;
      } else if (filterProfessional) {
        params.professional_id = filterProfessional;
      }
      if (filterType) params.appointment_type = filterType;
      const { data } = await axios.get(`${getApiUrl()}/clinic/appointments`, { params });
      setAppointments(data.data || []);
    } catch (e) { console.error(e); } finally { setLoadingAppts(false); }
  };

  const loadTodayAppointments = async () => {
    try {
      const today = formatDateStr(getSaoPauloDate());
      const params: any = { date_from: today, date_to: today, limit: 100 };
      // Se o usuário é profissional vinculado, filtrar somente seus agendamentos
      if (isProfessionalUser) {
        params.professional_id = userProfessionalId;
      }
      const { data } = await axios.get(`${getApiUrl()}/clinic/appointments`, { params });
      setTodayAppts(data.data || []);
    } catch (e) { console.error(e); }
  };

  const searchPatients = async (q: string) => {
    try { const { data } = await axios.get(`${getApiUrl()}/clinic/prescriptions-search/patients`, { params: { search: q } }); setPatientOptions(data.data || []); } catch (e) { console.error(e); }
  };

  const searchProfessionals = async (q: string) => {
    try { const { data } = await axios.get(`${getApiUrl()}/clinic/prescriptions-search/professionals`, { params: { search: q } }); setProfOptions(data.data || []); } catch (e) { console.error(e); }
  };

  const searchProceduresForNotes = async (q: string) => {
    try {
      const params: Record<string, string | number> = { limit: 120 };
      const t = q?.trim();
      if (t) params.search = t;
      const { data } = await axios.get(`${getApiUrl()}/clinic/procedures`, { params });
      const raw = Array.isArray(data?.data) ? data.data : [];
      setProcedureNoteOptions(raw.filter((p: any) => p?.is_active !== false));
    } catch (e) {
      console.error(e);
      setProcedureNoteOptions([]);
    }
  };

  const appendProcedureToAppointmentNotes = (proc: any) => {
    const name = String(proc?.name || '').trim();
    if (!name) return;
    const code = proc?.code != null ? String(proc.code).trim() : '';
    const line = code ? `${name} (${code})` : name;
    setForm((prev) => {
      const cur = (prev.notes || '').trim();
      return { ...prev, notes: cur ? `${cur}\n${line}` : line };
    });
  };

  const handleQuickPatientSave = async () => {
    if (!quickPatient.full_name.trim()) {
      setSnackbar({ open: true, message: 'Nome do paciente é obrigatório', severity: 'error' }); return;
    }
    try {
      setQuickPatientSaving(true);
      const payload: any = { full_name: quickPatient.full_name.trim() };
      if (quickPatient.cpf.trim()) payload.cpf = quickPatient.cpf.trim();
      if (quickPatient.phone.trim() && quickPatient.phone.trim() !== '55') payload.phone = quickPatient.phone.trim();
      if (quickPatient.whatsapp.trim() && quickPatient.whatsapp.trim() !== '55') payload.whatsapp = quickPatient.whatsapp.trim();
      if (quickPatient.email.trim()) payload.email = quickPatient.email.trim();
      if (quickPatient.birth_date) payload.birth_date = quickPatient.birth_date;
      const { data } = await axios.post(`${getApiUrl()}/clinic/patients`, payload);
      const newPatient = data.data || data;
      setPatientOptions([newPatient]);
      setForm(prev => ({ ...prev, patient_id: newPatient.id }));
      setQuickPatientOpen(false);
      setQuickPatient({ full_name: '', cpf: '', phone: '55', whatsapp: '55', email: '', birth_date: '' });
      setSnackbar({ open: true, message: 'Paciente cadastrado e selecionado!', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao cadastrar paciente', severity: 'error' });
    } finally {
      setQuickPatientSaving(false);
    }
  };

  /** Compara agenda original × formulário para marcar como reagendamento */
  const appointmentScheduleChanged = (appt: any) => {
    const origDate = String(appt.appointment_date || '');
    const origSt = String(appt.start_time || '').substring(0, 5);
    const origEt = String(appt.end_time || '').substring(0, 5);
    const origDur = Number(appt.duration_minutes) || 30;
    const st = String(form.start_time || '').substring(0, 5);
    const et = String(form.end_time || '').substring(0, 5);
    return (
      form.appointment_date !== origDate
      || st !== origSt
      || et !== origEt
      || Number(form.duration_minutes || 30) !== origDur
    );
  };

  const scrollToScheduleFields = () => {
    setRescheduleHint(true);
    window.setTimeout(() => {
      scheduleBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
  };

  const handleSave = async () => {
    if (!form.patient_id) {
      setSnackbar({ open: true, message: 'Selecione um paciente', severity: 'error' }); return;
    }
    try {
      if (editing) {
        const payload: Record<string, unknown> = { ...form };
        const changed = appointmentScheduleChanged(editing);
        if (changed) payload.status = 'rescheduled';
        await axios.put(`${getApiUrl()}/clinic/appointments/${editing.id}`, payload);
        setRescheduleHint(false);
        setSnackbar({ open: true, message: changed ? 'Consulta reagendada com sucesso!' : 'Consulta atualizada!', severity: 'success' });
      } else {
        await axios.post(`${getApiUrl()}/clinic/appointments`, form);
        setSnackbar({ open: true, message: 'Consulta agendada!', severity: 'success' });
      }
      setDialogOpen(false); resetForm(); refreshAll();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao salvar', severity: 'error' }); }
  };

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await axios.patch(`${getApiUrl()}/clinic/appointments/${id}/status`, { status });
      setSnackbar({ open: true, message: `Status: ${statusConfig[status]?.label || status}`, severity: 'success' });
      refreshAll();
    } catch (e: any) { setSnackbar({ open: true, message: e.response?.data?.error || 'Erro', severity: 'error' }); }
  };

  const confirmDelete = async () => {
    if (!apptToDelete) return;
    try {
      await axios.delete(`${getApiUrl()}/clinic/appointments/${apptToDelete.id}`);
      setSnackbar({ open: true, message: 'Excluído!', severity: 'success' });
      setDeleteDialogOpen(false); setApptToDelete(null); refreshAll();
    } catch (e) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const editingPatientHasWhatsApp = useMemo(() => {
    if (!editing) return false;
    const p =
      editing.clinic_patients ||
      patientOptions.find((x: { id: string }) => x.id === form.patient_id);
    const raw = String(p?.whatsapp || p?.phone || '').replace(/\D/g, '');
    return raw.length >= 10;
  }, [editing, patientOptions, form.patient_id]);

  const handleSendAppointmentConfirmation = async () => {
    if (!editing?.id) return;
    setSendingConfirmation(true);
    setConfirmationFeedback(null);
    try {
      const result = await sendAppointmentConfirmation(editing.id);
      if (result.success) {
        const msg = result.message || 'Confirmação enviada no WhatsApp do paciente.';
        setConfirmationFeedback({ severity: 'success', message: msg });
        setSnackbar({ open: true, message: msg, severity: 'success' });
      } else {
        const msg = result.error || 'Não foi possível enviar a confirmação.';
        setConfirmationFeedback({ severity: 'error', message: msg });
        setSnackbar({ open: true, message: msg, severity: 'error' });
      }
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Erro ao enviar confirmação.';
      setConfirmationFeedback({ severity: 'error', message: msg });
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSendingConfirmation(false);
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    const ws = getWeekStart(currentDate);
    const we = getWeekEnd(currentDate);
    await Promise.all([
      view === 'month'
        ? loadAppointments(formatDateStr(startOfMonth(currentDate)), formatDateStr(endOfMonth(currentDate)))
        : view === 'day'
          ? loadAppointments(formatDateStr(selectedDate), formatDateStr(selectedDate))
          : loadAppointments(formatDateStr(ws), formatDateStr(we)),
      loadTodayAppointments(),
      loadEvents(),
    ]);
    setRefreshing(false);
  };

  const resetForm = () => {
    setRescheduleHint(false);
    setEditing(null);
    setForm({
      patient_id: '',
      professional_id: isProfessionalUser ? userProfessionalId : '',
      appointment_date: formatDateStr(getSaoPauloDate()),
      start_time: '09:00', end_time: '09:30', duration_minutes: 30,
      appointment_type: 'consultation', title: '', notes: '', internal_notes: '',
      billing_mode: 'particular', total_amount: 0,
    });
    // Se é profissional, pré-carregar suas informações no autocomplete
    if (isProfessionalUser && userProfessionalId) {
      const myProf = allProfessionals.find((p: any) => p.id === userProfessionalId);
      if (myProf) setProfOptions([myProf]);
    }
  };

  const openNewAppt = (date: Date, hour?: number, minute: number = 0) => {
    resetForm();
    const ds = formatDateStr(date);
    const defaultSlotHour = agendaFirstHour <= 9 && agendaLastHour >= 9 ? 9 : agendaFirstHour;
    let hh = typeof hour === 'number' ? hour : defaultSlotHour;
    let mm = Math.max(0, Math.min(59, Math.round(minute)));
    let startAbs = hh * 60 + mm;
    const maxStartAbs = calEndExclusiveMin - SLOT_MIN;
    startAbs = Math.max(calStartAbsMin, Math.min(startAbs, maxStartAbs));
    startAbs = Math.round((startAbs - calStartAbsMin) / SLOT_MIN) * SLOT_MIN + calStartAbsMin;
    hh = Math.floor(startAbs / 60);
    mm = startAbs % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    const st = `${pad(hh)}:${pad(mm)}`;
    const defaultDuration = 30;
    const endTotal = hh * 60 + mm + defaultDuration;
    const endH = Math.floor(endTotal / 60) % 24;
    const endM = endTotal % 60;
    const et = `${pad(endH)}:${pad(endM)}`;
    setForm((f) => {
      const next = {
        ...f,
        appointment_date: ds,
        start_time: st,
        end_time: et,
        duration_minutes: defaultDuration,
      };
      if (defaultPatientId) {
        next.patient_id = defaultPatientId;
      }
      return next;
    });
    setDialogOpen(true);
  };

  const openEditAppt = (appt: any) => {
    setRescheduleHint(false);
    setConfirmationFeedback(null);
    setEditing(appt);
    setForm({
      patient_id: appt.patient_id || '', professional_id: appt.professional_id || '',
      appointment_date: appt.appointment_date || '', start_time: appt.start_time?.substring(0, 5) || '09:00',
      end_time: appt.end_time?.substring(0, 5) || '09:30', duration_minutes: appt.duration_minutes || 30,
      appointment_type: appt.appointment_type || 'consultation', title: appt.title || '',
      notes: appt.notes || '', internal_notes: appt.internal_notes || '',
      billing_mode: appt.billing_mode === 'plano' ? 'plano' : 'particular',
      total_amount: appt.total_amount || 0,
    });
    if (appt.clinic_patients) setPatientOptions([appt.clinic_patients]);
    if (appt.clinic_professionals) setProfOptions([appt.clinic_professionals]);
    setDialogOpen(true);
  };

  // Calendar helpers
  const getWeekStart = (d: Date) => { const dd = new Date(d); dd.setDate(dd.getDate() - dd.getDay()); return dd; };
  const getWeekEnd = (d: Date) => { const ws = getWeekStart(d); const we = new Date(ws); we.setDate(ws.getDate() + 6); return we; };
  const isSameDate = (d1: Date, d2: Date) => formatDateStr(d1) === formatDateStr(d2);

  const weekStart = getWeekStart(currentDate);
  const weekEnd = getWeekEnd(currentDate);
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
  const monthDays = eachDayOfInterval({ start: startOfMonth(currentDate), end: endOfMonth(currentDate) });

  const getActiveApptsForDate = (date: Date) => {
    const ds = formatDateStr(date);
    return appointments.filter((a) => a.appointment_date === ds && !isAgendaCancelled(a));
  };

  const getCancelledApptsForDate = (date: Date) => {
    const ds = formatDateStr(date);
    return appointments
      .filter((a) => a.appointment_date === ds && isAgendaCancelled(a))
      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  };

  const weekCancelledEntries = useMemo(
    () =>
      weekDays.flatMap((date) =>
        getCancelledApptsForDate(date).map((appt) => ({
          appt,
          dayLabel: format(date, "EEE, d 'de' MMM", { locale: ptBR }),
        })),
      ),
    [appointments, weekDays],
  );

  const monthCancelledEntries = useMemo(
    () =>
      monthDays.flatMap((date) =>
        getCancelledApptsForDate(date).map((appt) => ({
          appt,
          dayLabel: format(date, "d 'de' MMM", { locale: ptBR }),
        })),
      ),
    [appointments, monthDays],
  );

  const dayCancelledAppts = useMemo(
    () => getCancelledApptsForDate(selectedDate),
    [appointments, selectedDate],
  );

  const renderCancelledAgendaSection = (items: { appt: any; dayLabel?: string }[]) => {
    if (items.length === 0) return null;
    return (
      <Box
        sx={{
          borderTop: '2px dashed #fecaca',
          bgcolor: '#fef2f2',
          px: { xs: 1, sm: 1.5 },
          py: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: '#b91c1c', display: 'block', mb: 0.75, fontSize: '0.72rem' }}
        >
          Cancelados ({items.length}) — horário liberado na grade acima
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {items.map(({ appt, dayLabel }) => {
            const prof = appt.clinic_professionals?.full_name || '';
            return (
              <Box
                key={appt.id}
                onClick={() => openEditAppt(appt)}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.65,
                  borderRadius: 1,
                  border: '1px solid #fecaca',
                  bgcolor: '#fff',
                  cursor: 'pointer',
                  transition: '0.15s',
                  '&:hover': { bgcolor: '#fff5f5', borderColor: '#f87171' },
                }}
              >
                {dayLabel && (
                  <Chip
                    label={dayLabel}
                    size="small"
                    sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#fee2e2', color: '#991b1b' }}
                  />
                )}
                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                  {appt.start_time?.substring(0, 5)}
                  {appt.end_time ? `–${appt.end_time.substring(0, 5)}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#334155', flex: 1, minWidth: 120 }}>
                  {apptLabelWithNotes(appt, 'Paciente')}
                </Typography>
                {prof && (
                  <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>{prof}</Typography>
                )}
                <Chip
                  label={statusConfig.cancelled.label}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    bgcolor: statusConfig.cancelled.bg,
                    color: statusConfig.cancelled.color,
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderCancelledAgendaDock = (items: { appt: any; dayLabel?: string }[]) => {
    if (items.length === 0) return null;

    return (
      <Box
        sx={{
          flexShrink: 0,
          mt: 0.5,
          borderTop: '1px solid #fecaca',
          boxShadow: 'inset 0 1px 0 rgba(254,202,202,0.9)',
        }}
      >
        <Box
          sx={{
            bgcolor: '#fffaf9',
            px: { xs: 0.75, sm: 1 },
            py: 0.75,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: '#b91c1c', display: 'block', mb: 0.5, fontSize: '0.72rem' }}
          >
            Cancelados ({items.length}) — horário liberado na grade acima
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: 0.25,
            }}
          >
            {items.map(({ appt, dayLabel }) => (
              <Box
                key={appt.id}
                onClick={() => openEditAppt(appt)}
                sx={{
                  minWidth: { xs: 220, sm: 260 },
                  maxWidth: { xs: 260, sm: 320 },
                  px: 0.85,
                  py: 0.65,
                  borderRadius: 1.25,
                  border: '1px solid #fecaca',
                  bgcolor: '#fff5f5',
                  cursor: 'pointer',
                  transition: '0.15s',
                  '&:hover': { bgcolor: '#ffe4e6', borderColor: '#f87171' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.35, minWidth: 0 }}>
                  {dayLabel && (
                    <Chip
                      label={dayLabel}
                      size="small"
                      sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#fee2e2', color: '#991b1b', flexShrink: 0 }}
                    />
                  )}
                  <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#991b1b', fontVariantNumeric: 'tabular-nums' }}>
                    {appt.start_time?.substring(0, 5)}
                    {appt.end_time ? `–${appt.end_time.substring(0, 5)}` : ''}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    color: '#334155',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {apptLabelWithNotes(appt, 'Paciente')}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  };

  // KPI stats
  const todayTotal = todayAppts.length;
  const todayConfirmed = todayAppts.filter(a => ['confirmed', 'waiting', 'in_progress', 'attended', 'completed'].includes(a.status)).length;
  const todayPending = todayAppts.filter(a => a.status === 'scheduled').length;
  const todayCompleted = todayAppts.filter(a => a.status === 'completed').length;

  const getApptColor = (appt: any) => appointmentTypes[appt.appointment_type]?.color || '#14B8A6';

  const agendaScrollSx = {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
  };

  const renderMiniCalendarCardContent = (compact: boolean) => (
    <CardContent sx={{ p: compact ? 0.65 : 1, pt: compact ? 0.55 : 1, '&:last-child': { pb: compact ? 0.45 : 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: compact ? 0.35 : 0.75 }}>
        <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: compact ? '0.68rem' : '0.8rem', lineHeight: 1.2 }} noWrap>
          {format(currentDate, compact ? 'MMM yyyy' : "MMMM yyyy", { locale: ptBR })}
        </Typography>
        <Box sx={{ display: 'flex' }}>
          <IconButton size="small" onClick={() => setCurrentDate(subDays(currentDate, 30))} sx={{ color: '#64748b', p: compact ? 0.2 : 0.5 }}>
            <PrevIcon sx={{ fontSize: compact ? '0.85rem' : '0.95rem' }} />
          </IconButton>
          <IconButton size="small" onClick={() => setCurrentDate(addDays(currentDate, 30))} sx={{ color: '#64748b', p: compact ? 0.2 : 0.5 }}>
            <NextIcon sx={{ fontSize: compact ? '0.85rem' : '0.95rem' }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: compact ? 0.1 : 0.2 }}>
        {(compact ? ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] : ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']).map((d, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: compact ? 14 : 18, fontSize: compact ? '0.52rem' : '0.6rem', color: '#94a3b8', fontWeight: 700 }}>{d}</Box>
        ))}
        {Array.from({ length: startOfMonth(currentDate).getDay() }).map((_, i) => <Box key={`p-${i}`} />)}
        {monthDays.map((date, i) => {
          const hasAppts = getActiveApptsForDate(date).length > 0;
          const cellH = compact ? 21 : 24;
          const fs = compact ? '0.62rem' : '0.7rem';
          return (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: cellH,
                fontSize: fs,
                cursor: 'pointer',
                borderRadius: 0.65,
                position: 'relative',
                bgcolor: isToday(date) ? '#14B8A6' : 'transparent',
                color: isToday(date) ? '#fff' : '#0f172a',
                fontWeight: isToday(date) ? 700 : 400,
                '&:hover': { bgcolor: isToday(date) ? '#0d9488' : '#f1f5f9' },
              }}
              onClick={() => { setSelectedDate(date); setCurrentDate(date); setView('day'); }}
            >
              {format(date, 'd')}
              {hasAppts && !isToday(date) && <Box sx={{ position: 'absolute', bottom: 0.35, width: 2.5, height: 2.5, borderRadius: '50%', bgcolor: '#14B8A6' }} />}
            </Box>
          );
        })}
      </Box>
    </CardContent>
  );

  return (
    <Box sx={layoutExpanded ? {
      px: { xs: 0.75, sm: 1 },
      pt: 0,
      mt: 0,
      pb: 0,
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      maxHeight: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
    } : {
      px: { xs: 1, sm: 1.5 },
      pt: { xs: 1, sm: 1.5 },
      pb: 2,
      backgroundColor: 'transparent',
      minHeight: '100vh',
    }}>
      {/* KPIs compactos + ações — oculto na agenda expandida */}
      {!layoutExpanded && (
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 1, mb: 1.25 }}>
        <Grid container spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          {[
            { label: 'Hoje', value: todayTotal, color: '#14B8A6', icon: <AppointmentIcon sx={{ fontSize: 16 }} /> },
            { label: 'Confirm.', value: todayConfirmed, color: '#22c55e', icon: <ConfirmIcon sx={{ fontSize: 16 }} /> },
            { label: 'Pend.', value: todayPending, color: '#f59e0b', icon: <TimeIcon sx={{ fontSize: 16 }} /> },
            { label: 'Concl.', value: todayCompleted, color: '#3b82f6', icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> },
          ].map((kpi, i) => (
            <Grid item xs={3} sm={3} key={i}>
              <Card sx={{ bgcolor: 'white', borderRadius: 1.5, border: `1px solid ${kpi.color}18`, boxShadow: 'none' }}>
                <CardContent sx={{ py: 0.75, px: 1, '&:last-child': { pb: 0.75 }, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box sx={{ p: 0.4, borderRadius: 1, bgcolor: `${kpi.color}12`, color: kpi.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{kpi.icon}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.58rem', lineHeight: 1, display: 'block' }} noWrap>{kpi.label}</Typography>
                    <Typography sx={{ fontWeight: 800, color: kpi.color, fontSize: '1rem', lineHeight: 1.1 }}>{kpi.value}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', justifyContent: { xs: 'flex-end', sm: 'flex-end' }, flexShrink: 0 }}>
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={refreshAll} disabled={loadingAppts || refreshing} sx={{ color: '#14B8A6' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, fontWeight: 600, textTransform: 'none', py: 0.5 }}
            onClick={() => openNewAppt(getSaoPauloDate())}>
            Nova Consulta
          </Button>
        </Box>
      </Box>
      )}

      {(loadingAppts || eventsLoading) && <LinearProgress sx={{ mb: 1, '& .MuiLinearProgress-bar': { bgcolor: '#14B8A6' } }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>{error}</Alert>}

      {/* Navigation Controls */}
      <Card sx={{ mb: layoutExpanded ? 1 : 1.5, flexShrink: 0, borderRadius: 2, border: '1px solid #e2e8f0' }}>
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            {/* Left: Buttons */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ bgcolor: '#14B8A6', color: '#fff', '&:hover': { bgcolor: '#0d9488' } }}>
              Filtros
            </Button>
              {layoutExpanded && (
                <>
                  <Tooltip title="Atualizar">
                    <IconButton size="small" onClick={refreshAll} disabled={loadingAppts || refreshing} sx={{ color: '#14B8A6' }}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button size="small" variant="contained" startIcon={<AddIcon />}
                    sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, fontWeight: 600, textTransform: 'none', py: 0.5 }}
                    onClick={() => openNewAppt(getSaoPauloDate())}>
                    Nova Consulta
                  </Button>
                </>
              )}
          </Box>

            {/* Center: Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'center' }}>
              <IconButton onClick={() => {
                if (view === 'week') setCurrentDate(subWeeks(currentDate, 1));
                else if (view === 'day') {
                  const d = subDays(selectedDate, 1);
                  setSelectedDate(d);
                  setCurrentDate(d);
                } else setCurrentDate(subDays(currentDate, 30));
              }} sx={{ color: '#0f172a' }}><PrevIcon /></IconButton>
              <Button variant="outlined" onClick={() => { setCurrentDate(getSaoPauloDate()); setSelectedDate(getSaoPauloDate()); }}
                sx={{ borderColor: '#14B8A6', color: '#14B8A6', textTransform: 'none', fontWeight: 600 }}>Hoje</Button>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a', minWidth: 180, textAlign: 'center' }}>
                {format(view === 'day' ? selectedDate : currentDate, view === 'month' ? "MMMM 'de' yyyy" : "d 'de' MMMM yyyy", { locale: ptBR })}
            </Typography>
              <IconButton onClick={() => {
                if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
                else if (view === 'day') {
                  const d = addDays(selectedDate, 1);
                  setSelectedDate(d);
                  setCurrentDate(d);
                } else setCurrentDate(addDays(currentDate, 30));
              }} sx={{ color: '#0f172a' }}><NextIcon /></IconButton>
          </Box>

            {/* Right: View Toggle */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[
                { v: 'day' as const, icon: <TodayIcon />, label: 'Dia' },
                { v: 'week' as const, icon: <WeekIcon />, label: 'Semana' },
                { v: 'month' as const, icon: <MonthIcon />, label: 'Mês' },
              ].map(({ v, icon, label }) => (
                <Button key={v} size="small" variant={view === v ? 'contained' : 'outlined'} startIcon={icon}
                  onClick={() => setView(v)}
                  sx={view === v
                    ? { bgcolor: '#14B8A6', color: '#fff', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }
                    : { borderColor: '#e2e8f0', color: '#64748b', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                  {label}
            </Button>
              ))}
          </Box>
        </Box>

          {/* Filters */}
          {showFilters && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Profissional: só exibir filtro se NÃO for profissional vinculado */}
              {!isProfessionalUser ? (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Profissional</InputLabel>
                  <Select value={filterProfessional} label="Profissional" onChange={(e) => setFilterProfessional(e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {allProfessionals.map((p: any) => <MenuItem key={p.id} value={p.id}>{p.full_name}</MenuItem>)}
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  icon={<PersonIcon sx={{ fontSize: 16 }} />}
                  label={`Meus agendamentos: ${user?.professional?.full_name || user?.full_name || ''}`}
                  sx={{ bgcolor: '#ccfbf1', color: '#0d9488', fontWeight: 600, fontSize: '0.8rem' }}
                />
              )}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Tipo</InputLabel>
                <Select value={filterType} label="Tipo" onChange={(e) => setFilterType(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {Object.entries(appointmentTypes).map(([k, v]) => (
                    <MenuItem key={k} value={k}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderAppointmentTypeIcon(k, 18)}
                        {v.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!isProfessionalUser && (
                <Button size="small" onClick={() => { setFilterProfessional(''); setFilterType(''); }} sx={{ color: '#94a3b8' }}>Limpar</Button>
              )}
              {isProfessionalUser && filterType && (
                <Button size="small" onClick={() => { setFilterType(''); }} sx={{ color: '#94a3b8' }}>Limpar tipo</Button>
              )}
      </Box>
          )}
        </CardContent>
      </Card>

      {/* Main Layout: mini cal estreito + grade (expandida) | grade + sidebar (padrão) */}
      <Box sx={
        layoutExpanded
          ? {
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 1,
            flex: 1,
            minHeight: 0,
            alignItems: 'stretch',
          }
          : {
            display: 'flex',
            gap: { xs: 1.5, lg: 2 },
            minHeight: { xs: 'calc(100vh - 200px)', md: 'calc(100vh - 170px)' },
            alignItems: 'stretch',
          }
      }>
        {/* Calendar (grade principal — primeiro no fluxo: à esquerda em desktop, acima no mobile) */}
        <Card sx={{
          flex: 1,
          minWidth: 0,
          minHeight: layoutExpanded ? 0 : undefined,
          borderRadius: 2,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* WEEK VIEW */}
          {view === 'week' && (() => {
            const totalHeight = calHours * WEEK_HOUR_HEIGHT;
            const nowRaw = new Date().getHours() * 60 + new Date().getMinutes() - calStartAbsMin;
            const nowMinutes = isToday(new Date()) && nowRaw >= 0 && nowRaw < calTotalMin ? nowRaw : -1;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', ...agendaScrollSx }}>
                <Box sx={{ display: 'flex', flexShrink: 0, minHeight: 0 }}>
                {/* Time column */}
                <Box sx={{ width: TIME_COL_WEEK_PX, flexShrink: 0, borderRight: '2px solid rgba(20,184,166,0.25)', bgcolor: '#f8fafc' }}>
                  <Box sx={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '2px solid rgba(20,184,166,0.25)', position: 'sticky', top: 0, zIndex: 3, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" sx={{ color: '#14B8A6', fontWeight: 700, fontSize: '0.82rem', letterSpacing: 0.04 }}>HORA</Typography>
                  </Box>
                  <Box sx={{ position: 'relative', height: totalHeight }}>
                    {Array.from({ length: calHours }, (_, i) => i + agendaFirstHour).map((h, idx) => (
                      <Box key={h} sx={{ position: 'absolute', top: idx * WEEK_HOUR_HEIGHT, left: 0, right: 0, height: WEEK_HOUR_HEIGHT, borderBottom: idx === calHours - 1 ? 'none' : '1px solid rgba(20,184,166,0.18)' }}>
                        <Typography sx={{ position: 'absolute', top: 2, left: 3, right: 'auto', textAlign: 'left', fontSize: '0.86rem', fontWeight: 700, color: '#14B8A6', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                          {String(h).padStart(2, '0')}:00
                        </Typography>
                        <Typography sx={{ position: 'absolute', top: 30 * WEEK_PX_PER_MIN - 5, right: 3, left: 'auto', textAlign: 'right', fontSize: '0.72rem', fontWeight: 600, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                          :30
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                {/* Day columns */}
                <Box sx={{ flex: 1, display: 'flex', minWidth: 0 }}>
                  {weekDays.map((date, di) => {
                    const dayLayout = computeLayout(
                      clipApptsForCalendar(getActiveApptsForDate(date), calStartAbsMin, calEndExclusiveMin),
                      calStartAbsMin,
                    );
                    return (
                      <Box key={di} sx={{ flex: 1, minWidth: 0, borderRight: di < 6 ? '1px solid rgba(20,184,166,0.2)' : 'none' }}>
                        {/* Day header */}
                        <Box sx={{ height: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          borderBottom: '2px solid rgba(20,184,166,0.25)',
                          bgcolor: isToday(date) ? '#f0fdf4' : '#fafafa', position: 'sticky', top: 0, zIndex: 3, cursor: 'pointer' }}
                          onClick={() => { setSelectedDate(date); setView('day'); }}>
                          {isToday(date) && <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: '#14B8A6' }} />}
                          <Typography variant="caption" sx={{ fontWeight: 700, color: isToday(date) ? '#14B8A6' : '#64748b', fontSize: '0.65rem', textTransform: 'uppercase' }}>
                            {format(date, 'EEE', { locale: ptBR })}
                          </Typography>
                          <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: isToday(date) ? '#14B8A6' : 'transparent',
                            color: isToday(date) ? '#fff' : '#0f172a', fontSize: '0.8rem', fontWeight: 700 }}>
                            {format(date, 'd')}
                          </Box>
                        </Box>
                        {/* Slots */}
                        <Box
                          sx={{ position: 'relative', height: totalHeight, cursor: 'pointer',
                            bgcolor: 'white',
                            '&:hover': { bgcolor: 'rgba(20,184,166,0.04)' } }}
                          onClick={(e) => {
                            const el = e.currentTarget as HTMLElement;
                            const rect = el.getBoundingClientRect();
                            const y = e.clientY - rect.top;
                            const { hour, minute } = snapYToMinutes(y, WEEK_PX_PER_MIN);
                            openNewAppt(date, hour, minute);
                          }}>
                          {/* Gridlines */}
                          {Array.from({ length: calHours }, (_, i) => i).map(i => (
                            <React.Fragment key={i}>
                              <Box sx={{ position: 'absolute', left: 0, right: 0, top: i * WEEK_HOUR_HEIGHT, borderTop: '1px solid rgba(20,184,166,0.18)', pointerEvents: 'none' }} />
                              <Box sx={{ position: 'absolute', left: 0, right: 0, top: i * WEEK_HOUR_HEIGHT + 30 * WEEK_PX_PER_MIN, borderTop: '1px dashed rgba(148,163,184,0.3)', pointerEvents: 'none' }} />
                            </React.Fragment>
                          ))}
                          {/* Now indicator */}
                          {isToday(date) && nowMinutes >= 0 && nowMinutes < calTotalMin && (
                            <Box sx={{ position: 'absolute', left: 0, right: 0, top: nowMinutes * WEEK_PX_PER_MIN, zIndex: 5, pointerEvents: 'none' }}>
                              <Box sx={{ height: 2, bgcolor: '#ef4444' }} />
                              <Box sx={{ position: 'absolute', left: -4, top: -4, width: 10, height: 10, borderRadius: '50%', bgcolor: '#ef4444' }} />
                            </Box>
                          )}
                          {/* Appointments */}
                          {dayLayout.map(({ appt, topMin, durationMin, lane, totalLanes }) => {
                            const status = statusConfig[appt.status] || statusConfig.scheduled;
                            const widthPct = 100 / totalLanes;
                            const top = Math.max(0, topMin * WEEK_PX_PER_MIN);
                            const height = Math.max(16, durationMin * WEEK_PX_PER_MIN - 2);
                            return (
                              <Fade in key={appt.id}>
                                <Box onClick={(e) => { e.stopPropagation(); openEditAppt(appt); }}
                                  sx={{ position: 'absolute', top, height,
                                    left: `calc(${lane * widthPct}% + 2px)`,
                                    width: `calc(${widthPct}% - 4px)`,
                                    bgcolor: getApptColor(appt), color: '#fff', borderRadius: 1,
                                    px: 0.75, py: 0.35, cursor: 'pointer',
                                    boxShadow: `0 2px 6px ${getApptColor(appt)}40`,
                                    borderLeft: `3px solid ${status.color}`,
                                    overflow: 'hidden', transition: '0.15s',
                                    '&:hover': { filter: 'brightness(1.06)', zIndex: 10, boxShadow: `0 4px 12px ${getApptColor(appt)}60` } }}>
                                  <Typography sx={{
                                    fontSize: '0.7rem', fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word',
                                    display: height >= 34 ? '-webkit-box' : 'block',
                                    WebkitLineClamp: height >= 48 ? 3 : height >= 34 ? 2 : 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    whiteSpace: height >= 34 ? 'normal' as const : 'nowrap',
                                  }}>
                                    {appt.start_time?.substring(0, 5)} {apptLabelWithNotes(appt, 'Consulta')}
                                  </Typography>
                                  {height > 28 && (
                                    <Typography sx={{ fontSize: '0.6rem', opacity: 0.92, mt: 0.15, lineHeight: 1.15, wordBreak: 'break-word', display: height >= 40 ? '-webkit-box' : 'block', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                      {status.label}
                                    </Typography>
                                  )}
                                  {height >= 28 && (
                                    <Box
                                      sx={{ position: 'absolute', bottom: 2, right: 2, zIndex: 2, lineHeight: 0 }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {!embeddedInTeleconsult && (
                                      <TeleconsultButton appointmentId={appt.id} appointmentStatus={appt.status} compact />
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              </Fade>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                </Box>
              {renderCancelledAgendaDock(weekCancelledEntries)}
              </Box>
              </Box>
            );
          })()}

          {/* DAY VIEW */}
          {view === 'day' && (() => {
            const dayLayout = computeLayout(
              clipApptsForCalendar(getActiveApptsForDate(selectedDate), calStartAbsMin, calEndExclusiveMin),
              calStartAbsMin,
            );
            const dayHourHeight = layoutExpanded ? DAY_HOUR_HEIGHT_EXPANDED_LAYOUT : DAY_HOUR_HEIGHT;
            const dayPxPerMin = dayHourHeight / 60;
            const totalHeight = calHours * dayHourHeight;
            const now = new Date();
            const isSelectedToday = isSameDate(selectedDate, getSaoPauloDate());
            const nowRaw = now.getHours() * 60 + now.getMinutes() - calStartAbsMin;
            const nowMinutes = isSelectedToday && nowRaw >= 0 && nowRaw < calTotalMin ? nowRaw : -1;
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <Box sx={{ px: 1.5, py: 1, borderBottom: '2px solid rgba(20,184,166,0.25)', bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.75, flexShrink: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.88rem' }}>
                    {format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
                    <Typography component="span" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.75rem', ml: 1 }}>
                      ({getActiveApptsForDate(selectedDate).length} na grade
                      {dayCancelledAppts.length > 0 ? ` · ${dayCancelledAppts.length} cancelado(s)` : ''} · {SLOT_MIN} min)
                    </Typography>
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    <IconButton size="small" onClick={() => setSelectedDate(subDays(selectedDate, 1))} sx={{ color: '#14B8A6' }}><PrevIcon fontSize="small" /></IconButton>
                    <Button size="small" variant="outlined" onClick={() => setSelectedDate(getSaoPauloDate())}
                      sx={{ borderColor: '#14B8A6', color: '#14B8A6', textTransform: 'none', fontWeight: 600, py: 0.25, minWidth: 'auto', px: 1 }}>Hoje</Button>
                    <IconButton size="small" onClick={() => setSelectedDate(addDays(selectedDate, 1))} sx={{ color: '#14B8A6' }}><NextIcon fontSize="small" /></IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', ...agendaScrollSx }}>
                  <Box sx={{ display: 'flex', flexShrink: 0, minHeight: 0 }}>
                  {/* Time column com marcações de hora e 10 min */}
                  <Box sx={{ width: TIME_COL_DAY_PX, flexShrink: 0, borderRight: '2px solid rgba(20,184,166,0.25)', bgcolor: '#f8fafc', position: 'relative', height: totalHeight }}>
                    {Array.from({ length: calHours }, (_, i) => i + agendaFirstHour).map((h, idx) => (
                      <Box key={h} sx={{ position: 'absolute', top: idx * dayHourHeight, left: 0, right: 0, height: dayHourHeight, borderBottom: idx === calHours - 1 ? 'none' : '1px solid rgba(20,184,166,0.25)' }}>
                        <Typography sx={{ position: 'absolute', top: 3, left: 4, right: 'auto', textAlign: 'left', fontSize: '1rem', fontWeight: 800, color: '#14B8A6', letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums', lineHeight: 1.05 }}>
                          {String(h).padStart(2, '0')}:00
                        </Typography>
                        {[10, 20, 30, 40, 50].map(m => (
                          <Typography key={m} sx={{
                            position: 'absolute',
                            top: m * dayPxPerMin - 8,
                            right: 5,
                            left: 'auto',
                            textAlign: 'right',
                            fontSize: m === 30 ? '0.82rem' : '0.78rem',
                            fontWeight: m === 30 ? 700 : 600,
                            color: m === 30 ? '#475569' : '#64748b',
                            fontVariantNumeric: 'tabular-nums',
                            lineHeight: 1,
                          }}>
                            :{String(m).padStart(2, '0')}
                          </Typography>
                        ))}
                      </Box>
                    ))}
                  </Box>
                  {/* Slots container */}
                  <Box
                    sx={{ flex: 1, position: 'relative', height: totalHeight, cursor: 'pointer',
                      bgcolor: 'white',
                      '&:hover': { bgcolor: 'rgba(20,184,166,0.03)' } }}
                    onClick={(e) => {
                      const el = e.currentTarget as HTMLElement;
                      const rect = el.getBoundingClientRect();
                      const y = e.clientY - rect.top;
                      const { hour, minute } = snapYToMinutes(y, dayPxPerMin);
                      openNewAppt(selectedDate, hour, minute);
                    }}>
                    {/* Gridlines: hora (sólida), 30 min (tracejada), 10/20/40/50 (pontilhada sutil) */}
                    {Array.from({ length: calHours }, (_, i) => i).map(i => (
                      <React.Fragment key={i}>
                        <Box sx={{ position: 'absolute', left: 0, right: 0, top: i * dayHourHeight, borderTop: '1px solid rgba(20,184,166,0.22)', pointerEvents: 'none' }} />
                        <Box sx={{ position: 'absolute', left: 0, right: 0, top: i * dayHourHeight + 30 * dayPxPerMin, borderTop: '1px dashed rgba(148,163,184,0.38)', pointerEvents: 'none' }} />
                        {[10, 20, 40, 50].map(m => (
                          <Box key={m} sx={{ position: 'absolute', left: 0, right: 0, top: i * dayHourHeight + m * dayPxPerMin, borderTop: '1px dotted rgba(148,163,184,0.24)', pointerEvents: 'none' }} />
                        ))}
                      </React.Fragment>
                    ))}
                    {/* Now indicator */}
                    {isSelectedToday && nowMinutes >= 0 && nowMinutes < calTotalMin && (
                      <Box sx={{ position: 'absolute', left: 0, right: 0, top: nowMinutes * dayPxPerMin, zIndex: 5, pointerEvents: 'none' }}>
                        <Box sx={{ height: 2, bgcolor: '#ef4444' }} />
                        <Box sx={{ position: 'absolute', left: -6, top: -5, width: 12, height: 12, borderRadius: '50%', bgcolor: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,0.25)' }} />
                        <Typography sx={{ position: 'absolute', right: 8, top: -12, fontSize: '0.78rem', fontWeight: 700, color: '#ef4444', bgcolor: '#fff', px: 0.5, borderRadius: 0.5, fontVariantNumeric: 'tabular-nums', boxShadow: '0 0 0 1px rgba(239,68,68,0.2)' }}>
                          {format(now, 'HH:mm')}
                        </Typography>
                      </Box>
                    )}
                    {/* Appointments */}
                    {dayLayout.map(({ appt, topMin, durationMin, lane, totalLanes }) => {
                      const status = statusConfig[appt.status] || statusConfig.scheduled;
                      const widthPct = 100 / totalLanes;
                      const top = Math.max(0, topMin * dayPxPerMin);
                      const rawHeight = durationMin * dayPxPerMin - 2;
                      const height = Math.max(26, rawHeight);
                      const isShort = durationMin <= 15;
                      const isMedium = durationMin > 15 && durationMin <= 25;
                      const canWrapMain = !isShort && height >= 44;
                      const canWrapSub = !isShort && height >= 60;
                      return (
                        <Fade in key={appt.id}>
                          <Box onClick={(e) => { e.stopPropagation(); openEditAppt(appt); }}
                            sx={{
                              position: 'absolute',
                              top,
                              height,
                              left: `calc(${lane * widthPct}% + 6px)`,
                              width: `calc(${widthPct}% - 12px)`,
                              bgcolor: getApptColor(appt),
                              color: '#fff',
                              borderRadius: 1.5,
                              px: 1.25,
                              py: isShort ? 0.4 : 0.65,
                              cursor: 'pointer',
                              boxShadow: `0 2px 8px ${getApptColor(appt)}40`,
                              borderLeft: `4px solid ${status.color}`,
                              display: 'flex',
                              flexDirection: isShort ? 'row' : 'column',
                              alignItems: isShort ? 'center' : 'flex-start',
                              gap: isShort ? 0.75 : 0.35,
                              overflow: height >= 74 ? 'auto' : 'hidden',
                              transition: '0.15s',
                              minHeight: 0,
                              '&:hover': { filter: 'brightness(1.06)', zIndex: 10, boxShadow: `0 6px 20px ${getApptColor(appt)}60` },
                            }}>
                            {isShort ? (
                              <>
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2, flex: 1 }}>
                                  {appt.start_time?.substring(0, 5)} · {apptLabelWithNotes(appt, 'Consulta')}
                                </Typography>
                                <Chip label={status.label} size="small" sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, bgcolor: '#fff', color: status.color, border: `1px solid ${status.color}33`, flexShrink: 0, '& .MuiChip-label': { px: 0.5, py: 0 } }} />
                                {height >= 32 && (
                                  <Box
                                    sx={{ position: 'absolute', bottom: 4, right: 4, zIndex: 2, lineHeight: 0 }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {!embeddedInTeleconsult && (
                                    <TeleconsultButton appointmentId={appt.id} appointmentStatus={appt.status} compact />
                                    )}
                                  </Box>
                                )}
                              </>
                            ) : (
                              <>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.9, width: '100%', minWidth: 0 }}>
                                  <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(255,255,255,0.25)', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
                                    {(appt.clinic_patients?.full_name || 'P').charAt(0)}
                                  </Avatar>
                                  <Typography sx={{
                                    fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.25, flex: 1, minWidth: 0, wordBreak: 'break-word',
                                    display: canWrapMain ? '-webkit-box' : 'block',
                                    WebkitLineClamp: canWrapSub ? 4 : canWrapMain ? 2 : 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    whiteSpace: canWrapMain ? 'normal' as const : 'nowrap',
                                  }}>
                                    {apptLabelWithNotes(appt, 'Consulta')}
                                  </Typography>
                                  <Chip label={status.label} size="small" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#fff', color: status.color, border: `1px solid ${status.color}33`, flexShrink: 0, '& .MuiChip-label': { px: 0.65, py: 0 } }} />
                                </Box>
                                {!isMedium && height > 40 && (
                                  <Typography sx={{
                                    fontSize: '0.72rem', opacity: 0.95, width: '100%', lineHeight: 1.3, wordBreak: 'break-word',
                                    display: canWrapSub ? '-webkit-box' : 'block',
                                    WebkitLineClamp: canWrapSub ? 3 : 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    whiteSpace: canWrapSub ? 'normal' as const : 'nowrap',
                                  }}>
                                    {appt.start_time?.substring(0, 5)}–{appt.end_time?.substring(0, 5)} · {appointmentTypes[appt.appointment_type]?.label || 'Consulta'}
                                    {appt.clinic_professionals?.full_name ? ` · Dr(a). ${appt.clinic_professionals.full_name}` : ''}
                                  </Typography>
                                )}
                                {isMedium && (
                                  <Typography sx={{ fontSize: '0.68rem', opacity: 0.95, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
                                    {appt.start_time?.substring(0, 5)}–{appt.end_time?.substring(0, 5)} · {appointmentTypes[appt.appointment_type]?.label || 'Consulta'}
                                  </Typography>
                                )}
                                {height >= 32 && (
                                  <Box
                                    sx={{ position: 'absolute', bottom: 6, right: 6, zIndex: 2, lineHeight: 0 }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {!embeddedInTeleconsult && (
                                    <TeleconsultButton appointmentId={appt.id} appointmentStatus={appt.status} compact />
                                    )}
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        </Fade>
                      );
                    })}
                  </Box>
                  </Box>
                  {renderCancelledAgendaDock(
                    dayCancelledAppts.map((appt) => ({ appt })),
                  )}
                </Box>
              </Box>
            );
          })()}

          {/* MONTH VIEW */}
            {view === 'month' && (
            <Box sx={{ p: 2, ...(layoutExpanded ? { flex: 1, minHeight: 0, overflowY: 'auto' } : {}) }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                  <Box key={d} sx={{ p: 1.5, textAlign: 'center', fontWeight: 700, color: '#0f172a', fontSize: '0.75rem', textTransform: 'uppercase', borderBottom: '2px solid #14B8A622', bgcolor: '#f8fafc', borderRadius: 1 }}>
                    {d}
                    </Box>
                  ))}
                {/* Padding for first day of month */}
                {Array.from({ length: startOfMonth(currentDate).getDay() }).map((_, i) => (
                  <Box key={`pad-${i}`} sx={{ minHeight: 100 }} />
                ))}
                {monthDays.map((date, idx) => {
                  const dayAppts = getActiveApptsForDate(date);
                  const dayCancelled = getCancelledApptsForDate(date);
                    return (
                    <Box key={idx} onClick={() => { setSelectedDate(date); setView('day'); }}
                      sx={{ minHeight: 100, p: 1, border: '1px solid #f1f5f9', borderRadius: 2, cursor: 'pointer', transition: '0.2s', bgcolor: isToday(date) ? '#f0fdf4' : 'white',
                        '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: '#14B8A640' } }}>
                      <Typography sx={{ fontWeight: isToday(date) ? 700 : 500, color: isToday(date) ? '#14B8A6' : '#0f172a', fontSize: '0.85rem', mb: 0.5 }}>
                          {format(date, 'd')}
                        </Typography>
                      {dayAppts.slice(0, 3).map((appt) => {
                        const status = statusConfig[appt.status] || statusConfig.scheduled;
                        return (
                          <Box key={appt.id} sx={{ bgcolor: getApptColor(appt), color: '#fff', p: 0.5, mb: 0.3, borderRadius: 1, fontSize: '0.6rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 0.3 }}
                            onClick={(e) => { e.stopPropagation(); openEditAppt(appt); }}>
                            <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: status.color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                              {appt.start_time?.substring(0, 5)} {apptLabelWithNotes(appt, '')}
                                </Typography>
                            <Typography sx={{ fontSize: '0.5rem', opacity: 0.85, flexShrink: 0 }}>{status.label}</Typography>
                              </Box>
                        );
                      })}
                      {dayAppts.length > 3 && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 600 }}>+{dayAppts.length - 3} mais</Typography>
                        )}
                      {dayCancelled.length > 0 && (
                        <Typography sx={{ fontSize: '0.55rem', color: '#b91c1c', fontWeight: 600, mt: 0.25 }}>
                          {dayCancelled.length} cancelado(s)
                        </Typography>
                      )}
                      </Box>
                    );
                  })}
                </Box>
              {renderCancelledAgendaSection(monthCancelledEntries)}
              </Box>
            )}
        </Card>

        {/* Agenda expandida: mini cal à direita (desktop), topo da coluna lateral */}
        {layoutExpanded && (
          <Box
            sx={{
              width: { xs: '100%', lg: 196 },
              maxWidth: { xs: 'none', lg: 196 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignSelf: 'stretch',
              minHeight: 0,
            }}
          >
            <Card
              sx={{
                flexShrink: 0,
                alignSelf: 'stretch',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
              }}
            >
              {renderMiniCalendarCardContent(true)}
            </Card>
          </Box>
        )}

        {/* Sidebar direita: mini cal + hoje + legenda — só na agenda padrão */}
        {!layoutExpanded && (
        <Box sx={{ width: { lg: 280, xl: 300 }, flexShrink: 0, display: { xs: 'none', lg: 'flex' }, flexDirection: 'column', gap: 1 }}>
          <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
            {renderMiniCalendarCardContent(false)}
          </Card>

          {/* Today's Appointments List */}
          <Card sx={{ borderRadius: 2, border: '1px solid #e2e8f0', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, maxHeight: { lg: 'calc(100vh - 220px)' } }}>
            <CardContent sx={{ p: 1, py: 0.75, '&:last-child': { pb: 0.75 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0 }}>
                <AppointmentIcon sx={{ color: '#14B8A6', fontSize: '1rem' }} />
                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>Hoje</Typography>
                <Chip label={todayTotal} size="small" sx={{ ml: 'auto', bgcolor: '#14B8A615', color: '#14B8A6', fontWeight: 700, height: 20, fontSize: '0.65rem' }} />
                  </Box>
            </CardContent>
            <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1, minHeight: 0 }}>
              {todayAppts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>Nenhuma consulta hoje</Typography>
                  </Box>
              ) : (
                <>
                {[...todayAppts]
                  .filter((a) => !isAgendaCancelled(a))
                  .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                  .map((appt) => (
                  <Card key={appt.id} sx={{ mb: 1, borderRadius: 1.5, border: `1px solid ${getApptColor(appt)}22`, cursor: 'pointer', transition: '0.2s',
                    '&:hover': { boxShadow: `0 2px 8px ${getApptColor(appt)}12` } }}
                    onClick={() => openEditAppt(appt)}>
                    <CardContent sx={{ p: 1, py: 0.85, '&:last-child': { pb: 0.85 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                        <Box sx={{ width: 3, height: 28, borderRadius: 1.5, bgcolor: getApptColor(appt) }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {apptLabelWithNotes(appt, 'Paciente')}
                      </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>
                            {appt.start_time?.substring(0, 5)} - {appt.end_time?.substring(0, 5)} | {appt.clinic_professionals?.full_name || ''}
                      </Typography>
                    </Box>
                        <Chip label={statusConfig[appt.status]?.label || appt.status} size="small"
                          sx={{ bgcolor: statusConfig[appt.status]?.bg || '#f1f5f9', color: statusConfig[appt.status]?.color || '#64748b', fontWeight: 600, fontSize: '0.6rem', height: 22 }} />
                        </Box>
                      {/* Status Actions */}
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, pl: 2, flexWrap: 'wrap', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {appt.status === 'scheduled' && (
                          <Chip label="Confirmar" size="small" icon={<ConfirmIcon sx={{ fontSize: '0.7rem !important' }} />} onClick={() => handleStatusChange(appt.id, 'confirmed')}
                            sx={{ cursor: 'pointer', bgcolor: '#ccfbf1', color: '#14B8A6', fontWeight: 600, fontSize: '0.6rem', height: 20, '&:hover': { bgcolor: '#99f6e4' } }} />
                        )}
                        {appt.status === 'confirmed' && (
                          <Chip label="Check-in" size="small" icon={<CheckInIcon sx={{ fontSize: '0.7rem !important' }} />} onClick={() => handleStatusChange(appt.id, 'waiting')}
                            sx={{ cursor: 'pointer', bgcolor: '#fef3c7', color: '#f59e0b', fontWeight: 600, fontSize: '0.6rem', height: 20, '&:hover': { bgcolor: '#fde68a' } }} />
                        )}
                        {appt.status === 'waiting' && (
                          <Chip label="Iniciar" size="small" icon={<PlayIcon sx={{ fontSize: '0.7rem !important' }} />} onClick={() => handleStatusChange(appt.id, 'in_progress')}
                            sx={{ cursor: 'pointer', bgcolor: '#ede9fe', color: '#8b5cf6', fontWeight: 600, fontSize: '0.6rem', height: 20, '&:hover': { bgcolor: '#ddd6fe' } }} />
                        )}
                        {appt.status === 'in_progress' && (
                          <>
                            <Chip label="Atendido" size="small" icon={<DoneIcon sx={{ fontSize: '0.7rem !important' }} />} onClick={() => handleStatusChange(appt.id, 'attended')}
                              sx={{ cursor: 'pointer', bgcolor: '#ccfbf1', color: '#0d9488', fontWeight: 600, fontSize: '0.6rem', height: 20, '&:hover': { bgcolor: '#99f6e4' } }} />
                            <Chip label="Concluir" size="small" icon={<CheckCircleIcon sx={{ fontSize: '0.7rem !important' }} />} onClick={() => handleStatusChange(appt.id, 'completed')}
                              sx={{ cursor: 'pointer', bgcolor: '#dcfce7', color: '#22c55e', fontWeight: 600, fontSize: '0.6rem', height: 20, '&:hover': { bgcolor: '#bbf7d0' } }} />
                          </>
                        )}
                        {appt.status === 'attended' && (
                          <Chip label="Concluir" size="small" icon={<CheckCircleIcon sx={{ fontSize: '0.7rem !important' }} />} onClick={() => handleStatusChange(appt.id, 'completed')}
                            sx={{ cursor: 'pointer', bgcolor: '#dcfce7', color: '#22c55e', fontWeight: 600, fontSize: '0.6rem', height: 20, '&:hover': { bgcolor: '#bbf7d0' } }} />
                        )}
                        {!['completed', 'cancelled', 'no_show'].includes(appt.status) && (
                          <Chip label="Cancelar" size="small" onClick={() => handleStatusChange(appt.id, 'cancelled')}
                            sx={{ cursor: 'pointer', bgcolor: '#fee2e2', color: '#ef4444', fontWeight: 600, fontSize: '0.6rem', height: 20, '&:hover': { bgcolor: '#fecaca' } }} />
                        )}
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
                          {!embeddedInTeleconsult && (
                          <TeleconsultButton appointmentId={appt.id} appointmentStatus={appt.status} compact />
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                    </Card>
                ))}
                {todayAppts.filter(isAgendaCancelled).length > 0 && (
                  <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed #fecaca' }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#b91c1c', mb: 0.5 }}>
                      Cancelados hoje
                    </Typography>
                    {[...todayAppts]
                      .filter(isAgendaCancelled)
                      .sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''))
                      .map((appt) => (
                      <Card key={appt.id} sx={{ mb: 0.75, borderRadius: 1.5, border: '1px solid #fecaca', bgcolor: '#fef2f2', cursor: 'pointer' }}
                        onClick={() => openEditAppt(appt)}>
                        <CardContent sx={{ p: 0.85, py: 0.65, '&:last-child': { pb: 0.65 } }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#991b1b' }}>
                            {appt.start_time?.substring(0, 5)} · {apptLabelWithNotes(appt, 'Paciente')}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
                </>
              )}
            </Box>
          </Card>

          {/* Legend */}
          <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block' }}>TIPOS DE CONSULTA</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {Object.entries(appointmentTypes).map(([k, v]) => (
                  <Chip
                    key={k}
                    icon={renderAppointmentTypeIcon(k, 14)}
                    label={v.label}
                    size="small"
                    sx={{
                      bgcolor: `${v.color}15`,
                      color: v.color,
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 24,
                      '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
                    }}
                  />
                ))}
        </Box>
            </CardContent>
          </Card>
        </Box>
        )}
      </Box>

      {/* New/Edit Appointment Dialog */}
      {/* When editing an appointment with a patient → split layout (form + patient panel) */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setRescheduleHint(false); }}
        maxWidth={editing && form.patient_id ? false : 'sm'}
        fullWidth={!(editing && form.patient_id)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: '1px solid #e2e8f0',
            boxShadow: '0 24px 48px rgba(15, 23, 42, 0.12), 0 8px 16px rgba(15, 23, 42, 0.06)',
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden',
            maxHeight: editing && form.patient_id ? '92vh' : undefined,
            width: editing && form.patient_id ? 'calc(100vw - 64px)' : undefined,
            maxWidth: editing && form.patient_id ? 1600 : undefined,
            bgcolor: '#fff',
          },
        }}
      >
        {/* ── Left: appointment form (largura extra quando há painel do paciente ao lado) ── */}
        <Box sx={{
          width: editing && form.patient_id
            ? { xs: '100%', md: 540, lg: 580, xl: 620 }
            : '100%',
          minWidth: editing && form.patient_id ? { md: 520 } : undefined,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f7fa',
          borderRight: editing && form.patient_id ? '1px solid #e8ecf1' : 'none',
        }}>
        <DialogTitle
          sx={{
            bgcolor: '#fff',
            color: '#0f766e',
            fontWeight: 700,
            py: 2.25,
            px: 2.5,
            borderBottom: '1px solid #e8ecf1',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <MedicalIcon sx={{ color: '#0f766e' }} />
              {editing ? 'Editar Consulta' : 'Nova Consulta'}
            </Box>
            <IconButton
              onClick={() => { setDialogOpen(false); setRescheduleHint(false); }}
              sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, px: 2.5, bgcolor: '#f5f7fa' }}>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            {rescheduleHint && editing && (
              <Grid item xs={12}>
                <Alert severity="info" onClose={() => setRescheduleHint(false)} sx={{ py: 0.75 }}>
                  <strong>Reagendar:</strong> ajuste a <strong>data</strong>, <strong>início</strong>, <strong>fim</strong> ou <strong>duração</strong> abaixo e clique em <strong>Atualizar</strong>.
                  O status ficará como <em>Reagendado</em> quando o horário for alterado.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Autocomplete
              size="small" 
                options={patientOptions}
                getOptionLabel={(o: any) => o.full_name || ''}
                filterOptions={(x) => x}
                onInputChange={(_, v, reason) => { if (reason !== 'reset') searchPatients(v); }}
                onOpen={() => searchPatients('')}
                onChange={(_, v) => setForm({ ...form, patient_id: v?.id || '' })}
                value={patientOptions.find((p: any) => p.id === form.patient_id) || null}
                noOptionsText="Nenhum paciente encontrado"
                loadingText="Buscando pacientes..."
                renderOption={(props, o: any) => (
                  <li {...props} key={o.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: '#14B8A6', fontSize: '0.7rem' }}>{(o.full_name || 'P').charAt(0)}</Avatar>
                      <Box>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{o.full_name}</Typography>
                        {o.phone && <Typography variant="caption" sx={{ color: '#64748b' }}>{o.phone}</Typography>}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Paciente *"
                    InputProps={{ ...params.InputProps, startAdornment: <><PersonIcon sx={{ color: '#94a3b8', mr: 0.5, fontSize: '1.1rem' }} />{params.InputProps.startAdornment}</> }} />
                )}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
              />
              <Button size="small" startIcon={<PersonAddIcon />}
                onClick={() => { setQuickPatient({ full_name: '', cpf: '', phone: '55', whatsapp: '55', email: '', birth_date: '' }); setQuickPatientOpen(true); }}
                sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.78rem', color: '#14B8A6', fontWeight: 600 }}>
                Paciente não encontrado? Cadastrar novo
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                size="small"
                options={profOptions}
                getOptionLabel={(o: any) => o.full_name || ''}
                filterOptions={(x) => x}
                disabled={isProfessionalUser}
                onInputChange={(_, v, reason) => { if (reason !== 'reset') searchProfessionals(v); }}
                onOpen={() => searchProfessionals('')}
                onChange={(_, v) => setForm({ ...form, professional_id: v?.id || '' })}
                value={profOptions.find((p: any) => p.id === form.professional_id) || null}
                noOptionsText="Nenhum profissional encontrado"
                loadingText="Buscando profissionais..."
                renderOption={(props, o: any) => (
                  <li {...props} key={o.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, bgcolor: '#0d9488', fontSize: '0.7rem' }}>{(o.full_name || 'D').charAt(0)}</Avatar>
                      <Box>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{o.full_name}</Typography>
                        {o.specialty && <Typography variant="caption" sx={{ color: '#64748b' }}>{o.specialty}</Typography>}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label={isProfessionalUser ? 'Profissional (você)' : 'Profissional (opcional)'}
                    InputProps={{ ...params.InputProps, startAdornment: <><MedicalIcon sx={{ color: '#94a3b8', mr: 0.5, fontSize: '1.1rem' }} />{params.InputProps.startAdornment}</> }} />
                )}
                isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                ref={scheduleBlockRef}
                id="agenda-edit-schedule-block"
                sx={{
                  pt: 0.5,
                  pb: 0.25,
                  px: 1.25,
                  borderRadius: 2,
                  border: '1px solid rgba(20,184,166,0.35)',
                  bgcolor: rescheduleHint ? 'rgba(20,184,166,0.06)' : 'transparent',
                  transition: 'background 0.2s',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block', letterSpacing: 0.3 }}>
                  DATA E HORÁRIO
                </Typography>
                <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo de Consulta</InputLabel>
                <Select
                  value={form.appointment_type}
                  label="Tipo de Consulta"
                  onChange={(e) => setForm({ ...form, appointment_type: e.target.value })}
                  renderValue={(val) => {
                    const def = appointmentTypes[String(val)];
                    if (!def) return String(val);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderAppointmentTypeIcon(String(val), 18)}
                        {def.label}
                      </Box>
                    );
                  }}
                >
                  {Object.entries(appointmentTypes).map(([k, v]) => (
                    <MenuItem key={k} value={k}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderAppointmentTypeIcon(k, 20)}
                        {v.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" type="date" label="Data" value={form.appointment_date}
                onChange={(e) => setForm({ ...form, appointment_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="time" label="Início" value={form.start_time}
                onChange={(e) => {
                  const st = e.target.value;
                  const [h, m] = st.split(':').map(Number);
                  const em = h * 60 + m + (form.duration_minutes || 30);
                  const et = `${String(Math.floor(em / 60) % 24).padStart(2, '0')}:${String(em % 60).padStart(2, '0')}`;
                  setForm({ ...form, start_time: st, end_time: et });
                }} InputLabelProps={{ shrink: true }}
                inputProps={{ step: 600 }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="time" label="Fim" value={form.end_time}
                onChange={(e) => {
                  const et = e.target.value;
                  const [sh, sm] = form.start_time.split(':').map(Number);
                  const [eh, em] = et.split(':').map(Number);
                  const dur = Math.max(10, (eh * 60 + em) - (sh * 60 + sm));
                  setForm({ ...form, end_time: et, duration_minutes: dur });
                }} InputLabelProps={{ shrink: true }}
                inputProps={{ step: 600 }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Duração</InputLabel>
                <Select value={form.duration_minutes} label="Duração" onChange={(e) => {
                  const dur = Number(e.target.value);
                  const [h, m] = form.start_time.split(':').map(Number);
                  const em = h * 60 + m + dur;
                  const et = `${String(Math.floor(em / 60) % 24).padStart(2, '0')}:${String(em % 60).padStart(2, '0')}`;
                  setForm({ ...form, duration_minutes: dur, end_time: et });
                }}>
                  {[10, 15, 20, 30, 45, 60, 90, 120].map(d => <MenuItem key={d} value={d}>{d} min</MenuItem>)}
              </Select>
            </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Valor (R$)" value={form.total_amount}
                onChange={(e) => setForm({ ...form, total_amount: parseFloat(e.target.value) || 0 })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Título / Motivo" value={form.title} placeholder="Ex: Limpeza, Avaliação inicial..."
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                key={procedureNotePickerKey}
                size="small"
                options={procedureNoteOptions}
                filterOptions={(x) => x}
                getOptionLabel={(o: any) => String(o?.name || '')}
                isOptionEqualToValue={(a: any, b: any) => a?.id === b?.id}
                value={null}
                blurOnSelect
                onChange={(_, v: any | null) => {
                  if (v) {
                    appendProcedureToAppointmentNotes(v);
                    setProcedureNotePickerKey((k) => k + 1);
                  }
                }}
                onInputChange={(_, v, reason) => {
                  if (reason !== 'reset') void searchProceduresForNotes(v);
                }}
                onOpen={() => { void searchProceduresForNotes(''); }}
                noOptionsText="Nenhum procedimento cadastrado ou encontrado"
                renderOption={(props, o: any) => (
                  <li {...props} key={o.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>{o.name}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {o.code ? (
                          <Typography variant="caption" sx={{ color: '#64748b' }}>Cód.: {o.code}</Typography>
                        ) : null}
                        {o.clinic_procedure_categories?.name ? (
                          <Typography variant="caption" sx={{ color: '#14B8A6' }}>
                            {o.clinic_procedure_categories.name}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Procedimento (cadastrado)"
                    placeholder="Digite para buscar e selecione para inserir nas observações"
                    helperText="Cada seleção acrescenta uma linha ao campo Observações abaixo."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Cobrança</InputLabel>
                <Select
                  label="Cobrança"
                  value={form.billing_mode}
                  onChange={(e) => setForm({ ...form, billing_mode: e.target.value as 'particular' | 'plano' })}
                >
                  <MenuItem value="particular">Particular</MenuItem>
                  <MenuItem value="plano">Plano</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações" multiline rows={4} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Notas internas (não visíveis ao paciente)" multiline rows={2} value={form.internal_notes}
                onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} />
            </Grid>

            {/* Status actions for editing */}
            {editing && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1.25, display: 'block' }}>
                  ALTERAR STATUS
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {Object.entries(statusConfig).filter(([k]) => k !== editing.status).map(([k, v]) => (
                    <Chip key={k} label={v.label} size="small" onClick={() => { handleStatusChange(editing.id, k); setDialogOpen(false); refreshAll(); }}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: v.bg,
                        color: v.color,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 26,
                        borderRadius: '10px',
                        border: '1px solid transparent',
                        '&:hover': { filter: 'brightness(0.97)', boxShadow: '0 2px 6px rgba(15,23,42,0.06)' },
                      }} />
                    ))}
              </Box>
              </Grid>
            )}
            {editing && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block' }}>
                  CONFIRMAÇÃO WHATSAPP
                </Typography>
                <Tooltip
                  title={
                    editingPatientHasWhatsApp
                      ? 'Envia agora a mensagem de confirmação (mesmo template da confirmação automática)'
                      : 'Cadastre telefone ou WhatsApp do paciente'
                  }
                >
                  <span>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      startIcon={
                        sendingConfirmation ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <WhatsAppIcon sx={{ color: '#25D366' }} />
                        )
                      }
                      disabled={sendingConfirmation || !editingPatientHasWhatsApp}
                      onClick={() => void handleSendAppointmentConfirmation()}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#bbf7d0',
                        color: '#15803d',
                        fontWeight: 600,
                        justifyContent: 'flex-start',
                        '&:hover': { borderColor: '#25D366', bgcolor: 'rgba(37,211,102,0.06)' },
                      }}
                    >
                      Enviar confirmação de consulta
                    </Button>
                  </span>
                </Tooltip>
                {confirmationFeedback && (
                  <Alert
                    severity={confirmationFeedback.severity}
                    onClose={() => setConfirmationFeedback(null)}
                    sx={{ mt: 1, fontSize: 13 }}
                  >
                    {confirmationFeedback.message}
                  </Alert>
                )}
              </Grid>
            )}
            {editing && !embeddedInTeleconsult && (
              <Grid item xs={12} sx={{ overflow: 'visible' }}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', mb: 1, display: 'block' }}>
                  Teleconsulta por vídeo
                </Typography>
                <Box onClick={(e) => e.stopPropagation()} sx={{ position: 'relative', zIndex: 2 }}>
                  <TeleconsultButton appointmentId={editing.id} appointmentStatus={editing.status} />
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2.25,
            px: 2.5,
            borderTop: '1px solid #e8ecf1',
            bgcolor: '#fff',
            flexShrink: 0,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Button onClick={() => { setDialogOpen(false); setRescheduleHint(false); }} sx={{ color: '#64748b' }}>Cancelar</Button>
          {editing && (
            <>
              <Button variant="outlined" startIcon={<ScheduleIcon />} onClick={scrollToScheduleFields}
                sx={{ borderColor: '#f97316', color: '#ea580c', fontWeight: 600, mr: 'auto' }}>
                Reagendar
              </Button>
              <Button onClick={() => { setApptToDelete(editing); setDeleteDialogOpen(true); setDialogOpen(false); setRescheduleHint(false); }}
                sx={{ color: '#ef4444' }}>Excluir</Button>
            </>
          )}
          <Button variant="contained" onClick={handleSave} disabled={!form.patient_id}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, fontWeight: 600 }}>
            {editing ? 'Atualizar' : 'Agendar'}
          </Button>
        </DialogActions>
        </Box>{/* end left column */}

        {/* ── Right: Patient Panel ── */}
        {editing && form.patient_id && (
          <Box sx={{
            flex: 1,
            minWidth: { md: 480, lg: 560, xl: 640 },
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: '#fafbfc',
          }}>
            <PatientDetailPanel
              key={patientPanelKey}
              patientId={form.patient_id}
              initialPatient={editing?.clinic_patients ?? undefined}
              summaryReloadToken={patientPanelSummaryReload}
              onEdit={(p) => {
                setPatientForForm(p);
                setPatientFormOpen(true);
              }}
              onNewBudget={(p) => setBudgetModalPatient(p)}
            />
          </Box>
        )}
      </Dialog>

      {/* Modal de orçamento – abre sobre a página da agenda sem abrir nova aba */}
      <BudgetFormDialog
        open={!!budgetModalPatient}
        onClose={() => setBudgetModalPatient(null)}
        initialPatient={budgetModalPatient}
        onSaved={() => setPatientPanelSummaryReload((t) => t + 1)}
      />

      <PatientFormDialog
        open={patientFormOpen}
        onClose={() => setPatientFormOpen(false)}
        patientToEdit={patientForForm}
        onSaved={async () => {
          setPatientFormOpen(false);
          const pid = form.patient_id;
          if (pid) {
            try {
              const { data: res } = await axios.get(`${getApiUrl()}/clinic/patients/${pid}`);
              const p = res.success ? res.data : res;
              if (p && editing) {
                setEditing((prev: any) => (prev ? { ...prev, clinic_patients: p } : prev));
                setPatientOptions([p]);
              }
              setPatientPanelKey((k) => k + 1);
            } catch (e) { console.error(e); }
          }
          await refreshAll();
          setSnackbar({ open: true, message: 'Paciente atualizado com sucesso!', severity: 'success' });
        }}
        onError={(message) => setSnackbar({ open: true, message, severity: 'error' })}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600, color: '#0f172a' }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b' }}>Tem certeza que deseja excluir esta consulta?</Typography>
          {apptToDelete && (
            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fee2e2', borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 600, color: '#ef4444', fontSize: '0.85rem' }}>
                {apptLabelWithNotes(apptToDelete, 'Consulta')} - {apptToDelete.appointment_date} {apptToDelete.start_time?.substring(0, 5)}
          </Typography>
                  </Box>
                )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={confirmDelete} sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}>Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Patient Creation Dialog */}
      <Dialog open={quickPatientOpen} onClose={() => !quickPatientSaving && setQuickPatientOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon sx={{ color: '#14B8A6' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>Cadastro Rápido de Paciente</Typography>
          </Box>
          <IconButton onClick={() => !quickPatientSaving && setQuickPatientOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Nome Completo *" value={quickPatient.full_name}
                onChange={e => setQuickPatient(p => ({ ...p, full_name: e.target.value }))} disabled={quickPatientSaving} autoFocus />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="CPF" value={quickPatient.cpf}
                onChange={e => setQuickPatient(p => ({ ...p, cpf: e.target.value }))} disabled={quickPatientSaving}
                placeholder="000.000.000-00" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Data de Nascimento" type="date" value={quickPatient.birth_date}
                onChange={e => setQuickPatient(p => ({ ...p, birth_date: e.target.value }))} disabled={quickPatientSaving}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Telefone" value={quickPatient.phone}
                onChange={e => setQuickPatient(p => ({ ...p, phone: e.target.value }))} disabled={quickPatientSaving}
                placeholder="5511999999999" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="WhatsApp" value={quickPatient.whatsapp}
                onChange={e => setQuickPatient(p => ({ ...p, whatsapp: e.target.value }))} disabled={quickPatientSaving}
                placeholder="5511999999999" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="E-mail" type="email" value={quickPatient.email}
                onChange={e => setQuickPatient(p => ({ ...p, email: e.target.value }))} disabled={quickPatientSaving}
                placeholder="paciente@email.com" />
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2, borderRadius: 2, fontSize: '0.78rem' }}>
            Dados adicionais podem ser preenchidos depois na ficha completa do paciente.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setQuickPatientOpen(false)} disabled={quickPatientSaving} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleQuickPatientSave} disabled={quickPatientSaving || !quickPatient.full_name.trim()}
            startIcon={quickPatientSaving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <PersonAddIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            {quickPatientSaving ? 'Cadastrando...' : 'Cadastrar e Selecionar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Agenda; 
